import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Select, Button, Space, Table, message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import PageHeader from "../../components/common/PageHeader";
import reverseEngineeringService, { ReverseCustomer } from "../../services/reverseEngineering";

// Formata o intervalo de tempo retornado pelo PostgreSQL (objeto ou string) para HH:MM:SS.mmm
const formatInterval = (v: any): string => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const h = String(v.hours ?? 0).padStart(2, "0");
    const m = String(v.minutes ?? 0).padStart(2, "0");
    const s = String(Math.floor(v.seconds ?? 0)).padStart(2, "0");
    const ms = String(v.milliseconds ?? 0).padStart(3, "0");
    return `${h}:${m}:${s}.${ms}`;
  }
  return String(v);
};

const PushReverseEngineeringPage: React.FC = () => {
  const [customers, setCustomers] = useState<ReverseCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [searching, setSearching] = useState(false);
  const [pushData, setPushData] = useState<any[]>([]);
  const [cwData, setCwData] = useState<any[]>([]);
  const [pushError, setPushError] = useState<string | null>(null);
  const [cwError, setCwError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const load = async () => {
      setLoadingCustomers(true);
      try {
        const res = await reverseEngineeringService.listCustomers();
        setCustomers(res || []);
      } catch (err: any) {
        console.error("Erro ao carregar clientes", err);
        message.error("Erro ao carregar clientes para o dropdown");
      } finally {
        setLoadingCustomers(false);
      }
    };
    load();
  }, []);

  // Deduplica por customerId e exibe somente o nome do cliente
  const options = useMemo(() => {
    const seen = new Set<string>();
    return customers
      .filter((c) => {
        const cid = String(c.customerId ?? "");
        if (!cid || seen.has(cid)) return false;
        seen.add(cid);
        return true;
      })
      .map((c) => {
        const id = String(c.customerId ?? "");
        const name = String(c.customerName ?? "");
        const label = name ? `${name} (${id})` : id;
        return {
          label,
          value: id,
        };
      });
  }, [customers]);

  const handleClear = () => {
    form.resetFields();
    setPushData([]);
    setCwData([]);
    setPushError(null);
    setCwError(null);
  };

  const parseCnjValues = (valor: string | string[] | undefined): string[] => {
    if (!valor) return [];
    const items = Array.isArray(valor) ? valor : [valor];
    return items.flatMap((item) => String(item).split(",").map((s) => s.trim()).filter(Boolean));
  };

  const handleSearch = async (vals: any) => {
    const { cliente, valor } = vals;
    if (!cliente) {
      message.error("O filtro Cliente é obrigatório");
      return;
    }
    const cnjs = parseCnjValues(valor);
    if (cnjs.length === 0) {
      message.error("O filtro CNJ é obrigatório");
      return;
    }
    setSearching(true);
    try {
      // Chama rota que retorna ambos os resultados
      const res = await reverseEngineeringService.searchBoth({ customers: [String(cliente)], value: cnjs.join(",") });
      if (res) {
        if (res.push) {
          if (res.push.success) {
            setPushData(res.push.data || []);
            setPushError(null);
          } else {
            setPushData([]);
            setPushError(res.push.error || res.push.message || "Nenhum registro encontrado (Push)");
          }
        } else {
          setPushData([]);
          setPushError("Nenhum dado retornado (Push)");
        }

        if (res.cw) {
          if (res.cw.success) {
            setCwData(res.cw.data || []);
            setCwError(null);
          } else {
            setCwData([]);
            setCwError(res.cw.error || res.cw.message || "Nenhum registro encontrado (CW)");
          }
        } else {
          setCwData([]);
          setCwError("Nenhum dado retornado (CW)");
        }
      } else {
        setPushData([]);
        setCwData([]);
        message.warning("Nenhum dado retornado");
      }
    } catch (err: any) {
      console.error("Erro na busca", err);
      message.error("Erro ao executar busca");
      setPushData([]);
      setCwData([]);
      setPushError(err?.message || "Erro desconhecido");
      setCwError(err?.message || "Erro desconhecido");
    } finally {
      setSearching(false);
    }
  };

  const humanize = (s: string) =>
    s
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

  const PUSH_COLUMN_ORDER = ["pushId", "pushid", "cnj", "credentialId", "demandId", "createdAt", "deletedAt"];

  const pushColumns = useMemo(() => {
    if (!pushData || pushData.length === 0) return [];
    const keys = Object.keys(pushData[0]);
    const orderedKeys = [
      ...PUSH_COLUMN_ORDER.filter((k) => keys.includes(k)),
      ...keys.filter((k) => !PUSH_COLUMN_ORDER.includes(k)),
    ];
    const cols = orderedKeys.map((k) => ({
      title: k === "deletedAt" ? "Deleted At" : k === "cnj" ? "CNJ" : humanize(k),
      dataIndex: k,
      key: k,
      render: (v: any) => {
        if (k === "duracao") return formatInterval(v);
        if (k === "deletedAt" || k === "createdAt") {
          return v ? new Date(v).toLocaleString() : "-";
        }
        return v === null || v === undefined ? "-" : String(v);
      },
    }));
    return [
      ...cols,
      {
        title: "Ações",
        key: "actions",
        render: (_: any, record: any) => {
          const id = record.pushid || record.pushId || record.id;
          if (!id || record.deletedAt) return null;
          return (
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(`/push-details/${id}`, "_blank")}
            >
              Ver push
            </Button>
          );
        },
      },
    ];
  }, [pushData]);

  const cwColumns = useMemo(() => {
    if (!cwData || cwData.length === 0) return [];
    const keys = Object.keys(cwData[0]);
    return keys.map((k) => ({
      title: k === "deletedAt" ? "Deleted At" : humanize(k),
      dataIndex: k,
      key: k,
      render: (v: any) => {
        if (k === "duracao") return formatInterval(v);
        if (k === "deletedAt" || k === "createdAt") {
          return v ? new Date(v).toLocaleString() : "-";
        }
        return v === null || v === undefined ? "-" : String(v);
      },
    }));
  }, [cwData]);

  return (
    <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader title="Buscar ID" />
        </div>

        <Form form={form} layout="vertical" onFinish={handleSearch} style={{ marginTop: 12 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Form.Item name="cliente" rules={[{ required: true, message: "Cliente é obrigatório" }]} style={{ minWidth: 420 }}>
                <Select
                  placeholder="Selecione o cliente"
                  options={options}
                  loading={loadingCustomers}
                  showSearch
                  allowClear
                  // Filtrar por label (nome + id) ou value (id)
                  filterOption={(input, option: any) => {
                    const label = String(option?.label ?? "").toLowerCase();
                    const value = String(option?.value ?? "").toLowerCase();
                    return label.includes(input.toLowerCase()) || value.includes(input.toLowerCase());
                  }}
                />
              </Form.Item>

              <Form.Item name="valor" rules={[{ required: true, message: "CNJ é obrigatório" }]}>
                <Select
                  mode="tags"
                  placeholder="cnj"
                  style={{ width: 300 }}
                  tokenSeparators={[","]}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={searching}>Buscar</Button>
                  <Button onClick={handleClear}>Limpar</Button>
                </Space>
              </Form.Item>
            </div>
          </Space>
        </Form>

        <div style={{ marginTop: 16 }}>
          <Card size="small" title="Push" style={{ marginBottom: 12 }}>
            <Table
              dataSource={pushData}
              columns={pushColumns}
              rowKey={(record) => record.id || record.pushid || record.pushId || JSON.stringify(record)}
              pagination={{ pageSize: 30, showSizeChanger: false }}
              loading={searching}
            />
            {pushError ? <div style={{ color: "var(--ant-error-color)", marginTop: 8 }}>{pushError}</div> : null}
          </Card>

          <Card size="small" title="CW">
            <Table
              dataSource={cwData}
              columns={cwColumns}
              rowKey={(record) => record.id || record.requestid || JSON.stringify(record)}
              pagination={{ pageSize: 30, showSizeChanger: false }}
              loading={searching}
            />
            {cwError ? <div style={{ color: "var(--ant-error-color)", marginTop: 8 }}>{cwError}</div> : null}
          </Card>
        </div>
      </Card>
  );
};

export default PushReverseEngineeringPage;

